import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { activationKeyOptionsAtom } from 'views/Admin/Dashboard/CADashboard/CAList/state';

// SourceType enum - moved from PCCommon/state.ts
export enum SourceType {
  EP = 'endpoint_data',
  EP_EXTERNAL = 'endpoint_data__External',
  EP_INTERNAL = 'endpoint_data__Internal',
  EP_EC2 = 'endpoint_data__EC2',
  CA = 'ca_data',
  CA_SERVER = 'ca_data__Server',
  CA_WORKSTATIONS = 'ca_data__Workstations',
}

// Configuration for each source type - moved from PCCommon/state.ts
export const sourceTypeConfig = {
  [SourceType.CA]: {
    filterLabel: 'Cloud Agents',
    label: 'Cloud Agents',
    badgeLabel: 'Cloud Agents',
    iconClass: 'fas fa-cloud',
    icon: React.createElement('i', { className: 'fas fa-cloud' }),
    tooltip: 'Filter by specific cloud agents, search is supported.',
  },
  [SourceType.CA_SERVER]: {
    filterLabel: 'Cloud Agents (Servers)',
    label: 'Cloud Agents - Servers',
    badgeLabel: 'Cloud Agents',
    iconClass: 'fas fa-server',
    icon: React.createElement('i', { className: 'fas fa-server' }),
    tooltip: 'Filter by specific cloud agents, search is supported.',
  },
  [SourceType.CA_WORKSTATIONS]: {
    filterLabel: 'Cloud Agents (Workstations)',
    label: 'Cloud Agents - Workstations',
    badgeLabel: 'Cloud Agents',
    iconClass: 'fas fa-desktop',
    icon: React.createElement('i', { className: 'fas fa-desktop' }),
    tooltip: 'Filter by specific cloud agents, search is supported.',
  },
  [SourceType.EP]: {
    filterLabel: 'Endpoints',
    label: 'Endpoints',
    badgeLabel: 'Endpoints',
    iconClass: 'fas fa-desktop',
    icon: React.createElement('i', { className: 'fas fa-desktop' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
  },
  [SourceType.EP_INTERNAL]: {
    filterLabel: 'Endpoints (Internal)',
    label: 'Endpoints - Internal',
    badgeLabel: 'Endpoints',
    iconClass: 'fas fa-network-wired',
    icon: React.createElement('i', { className: 'fas fa-network-wired' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
  },
  [SourceType.EP_EXTERNAL]: {
    filterLabel: 'Endpoints (External)',
    label: 'Endpoints - External',
    badgeLabel: 'Endpoints',
    iconClass: 'fas fa-desktop',
    icon: React.createElement('i', { className: 'fas fa-desktop' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
  },
  [SourceType.EP_EC2]: {
    filterLabel: 'Endpoints (AWS)',
    label: 'Endpoints - AWS',
    badgeLabel: 'Endpoints',
    iconClass: 'fab fa-aws',
    icon: React.createElement('i', { className: 'fab fa-aws' }),
    tooltip: 'Filter by specific endpoints, search is supported.',
  },
};

const epOptions = [
  {
    value: SourceType.EP,
    label: 'Endpoints - All',
    icon: sourceTypeConfig[SourceType.EP].iconClass,
    level: 0,
  },
  {
    value: SourceType.EP_EXTERNAL,
    label: 'Endpoints - External',
    icon: 'fas fa-external-link-alt',
    level: 0,
  },
  {
    value: SourceType.EP_INTERNAL,
    label: 'Endpoints - Internal',
    icon: sourceTypeConfig[SourceType.EP_INTERNAL].iconClass,
    level: 0,
  },
  {
    value: SourceType.EP_EC2,
    label: 'Endpoints - AWS',
    icon: sourceTypeConfig[SourceType.EP_EC2].iconClass,
    level: 0,
  },
];

const caHostOptions = [
  {
    value: SourceType.CA,
    label: 'Cloud Agents - All',
    icon: sourceTypeConfig[SourceType.CA].iconClass,
    level: 0,
  },
  {
    value: SourceType.CA_SERVER,
    label: 'Cloud Agents - Servers',
    icon: sourceTypeConfig[SourceType.CA_SERVER].iconClass,
    level: 0,
  },
  {
    value: SourceType.CA_WORKSTATIONS,
    label: 'Cloud Agents - Workstations',
    icon: sourceTypeConfig[SourceType.CA_WORKSTATIONS].iconClass,
    level: 0,
  },
];

interface PCHostTypeDropdownProps {
  value: string; // Current sourceType value
  agentKey: string; // Current agentKey value
  onChange: (sourceType: string, agentKey: string) => void; // Callback when selection changes
  onSourceTypeChange?: () => void; // Optional callback for additional reset logic
}

const PCHostTypeDropdown: React.FC<PCHostTypeDropdownProps> = ({
  value,
  agentKey,
  onChange,
  onSourceTypeChange,
}) => {
  const agentActivationKeysOptions = useAtomValue(activationKeyOptionsAtom);
  // Add state to track the selected option ID
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hostTypeOptions, setHostTypeOptions] = useState<
    {
      level: number;
      icon: string;
      value: string;
      label: string;
      agentKey?: string;
      id: string;
    }[]
  >([]);

  useEffect(() => {
    const fetchHostOptions = async () => {
      const formattedAgentKeys = agentActivationKeysOptions?.map(
        (option, _index) => ({
          ...option,
          value: option.label.toLowerCase().includes('workstation')
            ? SourceType.CA_WORKSTATIONS
            : SourceType.CA_SERVER,
          level: 1, // Indentation level
          icon: option.label.toLowerCase().includes('workstation')
            ? sourceTypeConfig[SourceType.CA_WORKSTATIONS].iconClass
            : sourceTypeConfig[SourceType.CA_SERVER].iconClass, // Icon for each option
          agentKey: option.value,
          id: `agent-${option.value}`, // Add unique ID
        }),
      );

      // Filter options containing "Servers" and "Workstations"
      const workstationOptions = formattedAgentKeys?.filter((opt) =>
        opt.label.toLowerCase().includes('workstation'),
      );
      const serverOptions = formattedAgentKeys?.filter(
        (opt) => !opt.label.toLowerCase().includes('workstation'),
      );

      // Add unique IDs to EP options
      const _epOptionsWithIds = epOptions.map((opt) => ({
        ...opt,
        id: `ep-${opt.value}`,
      }));

      // Add unique IDs to CA host options
      const caOptions = caHostOptions.map((opt) => ({
        ...opt,
        id: `ca-${opt.value}`,
      }));

      // Merge the lists in the correct order
      const newHostTypeOptions = [
        caOptions[0], // "Cloud Agents - All"
        caOptions[1], // "CA All Servers"
        ...(serverOptions || []), // Add all "Servers" options here
        caOptions[2], // "CA All Workstations"
        ...(workstationOptions || []), // Add all "Workstations" options here
        // ..._epOptionsWithIds, // Endpoint options
      ];
      setHostTypeOptions(newHostTypeOptions);
    };

    void fetchHostOptions();
  }, [agentActivationKeysOptions]);

  // Initialize selected ID when options change
  useEffect(() => {
    if (hostTypeOptions.length > 0) {
      // Find the current option based on value and agentKey props
      const currentOption =
        agentKey !== ''
          ? hostTypeOptions.find(
              (option) =>
                option.value === value && option.agentKey === agentKey,
            )
          : hostTypeOptions.find((option) => option.value === value);

      if (currentOption) {
        setSelectedId(currentOption.id);
      }
    }
  }, [hostTypeOptions, value, agentKey]);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: 'white',
      cursor: 'pointer',
    }),
    option: (styles, { isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
            ? '#007bff' // Bright blue for selected item
            : isFocused
              ? '#f0f0f0' // Light gray for hover
              : undefined,
        color: isSelected ? 'white' : undefined, // White text for selected item
        cursor: isDisabled ? 'not-allowed' : 'pointer', // Pointer cursor on hover
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 0.2s ease', // Smooth transition for hover effect
        '&:active': {
          backgroundColor: '#0069d9', // Darker blue when clicked
        },
      };
    },
    singleValue: (styles) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'middle',
    }),
  };

  const customSingleValue = ({ data }) => (
    <div
      style={{
        display: 'block',
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'middle',
      }}
    >
      <i className={data.icon} style={{ marginRight: 5 }} />
      {data.label}
    </div>
  );

  const customOption = (props) => {
    const { data, innerRef, innerProps, isSelected, isFocused } = props;
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          paddingLeft: `${8 + data.level * 15}px`, // Increased indentation for better hierarchy
          backgroundColor: isSelected
            ? '#007bff'
            : isFocused
              ? '#f0f0f0'
              : 'transparent',
          color: isSelected ? 'white' : 'inherit',
          cursor: 'pointer',
          borderRadius: '4px',
          margin: '2px 0',
        }}
      >
        <i
          className={data.icon}
          style={{
            marginRight: 10,
            color: isSelected ? 'white' : '#6c757d', // Icon color
          }}
        />
        {data.label}
      </div>
    );
  };

  const handleChange = (selectedOption) => {
    const sourceTypeChanged = value !== selectedOption.value;
    const agentKeyChanged = selectedOption.agentKey !== agentKey;

    // Always call onChange with the new values
    onChange(selectedOption.value, selectedOption.agentKey || '');
    setSelectedId(selectedOption.id);

    // Call the optional reset callback if source type changed
    if ((sourceTypeChanged || agentKeyChanged) && onSourceTypeChange) {
      onSourceTypeChange();
    }
  };

  return (
    <Select
      className="custom-360-dropdown text-left mb-2"
      styles={colourStyles}
      options={hostTypeOptions}
      value={
        hostTypeOptions.find((option) => option.id === selectedId) ||
        (agentKey !== ''
          ? hostTypeOptions.find(
              (option) =>
                option.value === value && option.agentKey === agentKey,
            )
          : hostTypeOptions.find((option) => option.value === value))
      }
      onChange={handleChange}
      components={{ SingleValue: customSingleValue, Option: customOption }}
      isSearchable={false}
      getOptionValue={(option) => option.id} // Use the unique ID as the value
    />
  );
};

export default PCHostTypeDropdown;
